// Miky Motorsport - global constants (zero dependencies)
// Konvence: SI jednotky ve fyzice (metry, sekundy, newtony, radiany).
//           Rychlost hraci zobrazujeme v km/h (MS_TO_KMH).

// ---------------------------------------------------------------------------
// Game loop (fixed timestep accumulator - prevzato ze serie Miky)
// ---------------------------------------------------------------------------
export const TICK_RATE = 60;
export const FIXED_DT = 1 / TICK_RATE;        // 0.016666... s
export const TICK_MS = 1000 / TICK_RATE;      // 16.666... ms
export const MAX_FRAME_SKIP = 5;              // pojistka proti spirale smrti

// ---------------------------------------------------------------------------
// Jednotky / prevody
// ---------------------------------------------------------------------------
export const MS_TO_KMH = 3.6;
export const KMH_TO_MS = 1 / 3.6;
export const DEG_TO_RAD = Math.PI / 180;
export const RAD_TO_DEG = 180 / Math.PI;

// ---------------------------------------------------------------------------
// Vstup: akce -> klavesy (KeyboardEvent.code)
// ---------------------------------------------------------------------------
export const ACTION = Object.freeze({
  THROTTLE: 'THROTTLE',
  BRAKE: 'BRAKE',
  STEER_LEFT: 'STEER_LEFT',
  STEER_RIGHT: 'STEER_RIGHT',
  HANDBRAKE: 'HANDBRAKE',
  BOOST: 'BOOST',
  REWIND: 'REWIND',
  CAMERA: 'CAMERA',
  PAUSE: 'PAUSE',
  RESET: 'RESET',
  GEAR_UP: 'GEAR_UP',
  GEAR_DOWN: 'GEAR_DOWN',
  QUIT: 'QUIT',
  RESTART: 'RESTART',
});

// code -> [actions]
export const KEY_MAP = Object.freeze({
  ArrowUp: [ACTION.THROTTLE],
  KeyW: [ACTION.THROTTLE],
  ArrowDown: [ACTION.BRAKE],
  KeyS: [ACTION.BRAKE],
  ArrowLeft: [ACTION.STEER_LEFT],
  KeyA: [ACTION.STEER_LEFT],
  ArrowRight: [ACTION.STEER_RIGHT],
  KeyD: [ACTION.STEER_RIGHT],
  Space: [ACTION.HANDBRAKE],
  ShiftLeft: [ACTION.BOOST],
  ShiftRight: [ACTION.BOOST],
  KeyR: [ACTION.REWIND],
  KeyC: [ACTION.CAMERA],
  Escape: [ACTION.PAUSE],
  KeyT: [ACTION.RESET],
  KeyE: [ACTION.GEAR_UP],
  KeyQ: [ACTION.GEAR_DOWN],
  KeyM: [ACTION.QUIT],
  Enter: [ACTION.RESTART],
});

// ---------------------------------------------------------------------------
// Input shaping (konkretni cisla z planu v3 - klavesnice/gamepad hratelnost)
// ---------------------------------------------------------------------------
export const INPUT = Object.freeze({
  // Rizeni: max uhel rejdu klesa s rychlosti (speed-sensitive steering)
  STEER_MAX_DEG: 40,            // max rejd v nizke rychlosti
  STEER_MIN_DEG: 8,             // max rejd v plne rychlosti
  STEER_FALLOFF_KMH: 180,       // rychlost pri ktere dosahneme STEER_MIN_DEG
  STEER_RATE_DPS: 220,          // rychlost najezdu k cili rejdu (deg/s)
  STEER_RETURN_DPS: 260,        // rychlost navratu do stredu pri pusteni (deg/s)
  // Plyn/brzda ramp (simulace analogoveho pedalu na klavesnici)
  THROTTLE_RAMP_S: 0.20,        // cas 0->1
  THROTTLE_RELEASE_S: 0.12,
  BRAKE_RAMP_S: 0.15,
  BRAKE_RELEASE_S: 0.10,
  // Gamepad
  GAMEPAD_DEADZONE: 0.12,
  GAMEPAD_STEER_EXPO: 2.0,      // kvadraticka krivka (jemnost u stredu)
});

// ---------------------------------------------------------------------------
// Fyzika sveta
// ---------------------------------------------------------------------------
export const PHYS = Object.freeze({
  GRAVITY: -9.81,
  CCD_SUBSTEPS: 4,              // IntegrationParameters.num_ccd_substeps (anti-tunneling)
  SUBSTEPS: 1,                  // micro-stepping kola resi stabilitu, fyzika 1/60
});

// ---------------------------------------------------------------------------
// Object pooly
// ---------------------------------------------------------------------------
export const POOL = Object.freeze({
  PARTICLES: 600,              // smyk kour, prach, jiskry
});

// ---------------------------------------------------------------------------
// Kamera
// ---------------------------------------------------------------------------
export const CAMERA = Object.freeze({
  FOV_BASE: 62,                // stupne pri stani
  FOV_MAX: 74,                 // pri plne rychlosti (sirsi zaber = vetsi pocit rychlosti)
  FOV_SPEED_KMH: 230,          // rychlost pri ktere dosahneme FOV_MAX
  // Chase view offset (za autem, lokalni souradnice: x vpravo, y nahoru, z dozadu)
  CHASE_BACK: 6.0,
  CHASE_BACK_MAX: 7.6,         // odstup kamery pri plne rychlosti (pull-back na rychlosti)
  DIST_SPEED_KMH: 230,         // rychlost pri ktere dosahneme CHASE_BACK_MAX
  CHASE_HEIGHT: 2.5,
  CHASE_LOOK_AHEAD: 4.0,
  LOOK_LATERAL: 0.9,           // bocni posun look-at do zatacky pri plnem rizeni (m) - jemne
  LEAN_MAX_RAD: 0.028,         // max naklon (roll) kamery do zatacky (~1.6 st.) - jen naznak
  LEAN_REF_KMH: 120,           // rychlost pri ktere je naklon/look-ahead plny
  FOLLOW_STIFFNESS: 11.0,      // pruzinovy lerp (vyssi = tuzsi sledovani, kamera neutika pri akceleraci)
});

// A4: near-miss (tesne minuti AI) - svist + maly FOV spike, s cooldownem proti spamu.
export const NEAR_MISS = Object.freeze({
  DIST: 3.6,        // m (horizontalni vzdalenost stred-stred)
  COOLDOWN: 0.45,   // s mezi svisty
  MIN_KMH: 35,      // pod tuto rychlost zadny near-miss
});

// A5: sila vignety dle rychlosti (pocit rychlosti). BASE = puvodni staticka hodnota.
export const VIGNETTE = Object.freeze({
  BASE: 0.42,
  SPEED: 0.18,   // pridana sila pri >= REF_KMH
  REF_KMH: 230,
});

// Pohledy kamery (C cykluje)
export const CAMERA_VIEW = Object.freeze({
  CHASE: 'CHASE',
  HOOD: 'HOOD',
  BUMPER: 'BUMPER',
});
export const CAMERA_VIEW_ORDER = [CAMERA_VIEW.CHASE, CAMERA_VIEW.HOOD, CAMERA_VIEW.BUMPER];
